import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';

const TRACK_SX = {
  gap: 0.5,
  p: 0.5,
  borderRadius: 0.6,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  alignSelf: 'flex-start',
};

const OPTION_SX = {
  px: 2.5,
  py: 1,
  border: 0,
  borderRadius: 0.6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  bgcolor: 'transparent',
  color: 'text.secondary',
};

const OPTION_ON_SX = { bgcolor: 'background.icon', color: 'common.white', fontWeight: 600 };

export interface SegmentedOption<T> {
  id: T;
  label: string;
}

export interface SegmentedControlProps<T> {
  label: string;
  options: Array<SegmentedOption<T>>;
  value: T;
  onChange: (id: T) => void;
  role?: 'radio' | 'tab';
}

export function SegmentedControl<T extends string | number>({
  label,
  options,
  value,
  onChange,
  role = 'radio',
}: SegmentedControlProps<T>): ReactNode {
  return (
    <Stack
      direction="row"
      role={role === 'tab' ? 'tablist' : 'radiogroup'}
      aria-label={label}
      sx={TRACK_SX}
    >
      {options.map((option) => (
        <Box
          key={String(option.id)}
          component="button"
          type="button"
          role={role}
          {...(role === 'tab'
            ? { 'aria-selected': option.id === value }
            : { 'aria-checked': option.id === value })}
          onClick={() => onChange(option.id)}
          sx={[OPTION_SX, option.id === value && OPTION_ON_SX]}
        >
          {option.label}
        </Box>
      ))}
    </Stack>
  );
}
